import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { Input, Button, Toggle } from '../design-system';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface QuickReplySetFormData {
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface QuickReplySetFormProps {
  initialData?: Partial<QuickReplySetFormData>;
  onSubmit: (data: QuickReplySetFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  hideActions?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
}

export const QuickReplySetForm: React.FC<QuickReplySetFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save Reply Set',
  loading = false,
  hideActions = false,
  submitRef,
}) => {
  const styles = useThemedStyles(getStyles);
  const [formData, setFormData] = useState<QuickReplySetFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
    isDefault: initialData?.isDefault || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        isDefault: initialData.isDefault || false,
      });
    }
  }, [initialData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Quick reply set name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Quick reply set name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  // Expose submit function via ref
  React.useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateField = (field: keyof QuickReplySetFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.form}>
        {/* Name */}
        <View style={styles.field}>
          <Input
            label="Reply Set Name *"
            value={formData.name}
            onChangeText={(text) => updateField('name', text)}
            placeholder="e.g., Welcome Messages"
            error={errors.name}
            autoCapitalize="words"
            maxLength={100}
          />
        </View>

        {/* Description */}
        <View style={styles.field}>
          <Input
            label="Description"
            value={formData.description}
            onChangeText={(text) => updateField('description', text)}
            placeholder="Brief description of this quick reply set"
            multiline
            numberOfLines={3}
            maxLength={500}
          />
        </View>

        {/* Active Status */}
        <View style={styles.field}>
          <View style={styles.switchContainer}>
            <Text style={styles.switchLabel}>Active</Text>
            <Text style={styles.switchHelper}>
              {formData.isActive
                ? 'This quick reply set is active and can be used'
                : 'This quick reply set is inactive and hidden'}
            </Text>
          </View>
          <View style={styles.toggleContainer}>
            <Toggle
              value={formData.isActive}
              onValueChange={(value) => updateField('isActive', value)}
            />
          </View>
        </View>

        {/* Default Status */}
        <View style={styles.field}>
          <View style={styles.switchContainer}>
            <Text style={styles.switchLabel}>Default</Text>
            <Text style={styles.switchHelper}>
              {formData.isDefault
                ? 'This set will be automatically sent when contacts reach the channel'
                : 'This set will not be sent automatically'}
            </Text>
          </View>
          <View style={styles.toggleContainer}>
            <Toggle
              value={formData.isDefault}
              onValueChange={(value) => updateField('isDefault', value)}
            />
          </View>
        </View>

        {/* Actions */}
        {!hideActions && (
        <View style={styles.actions}>
          {onCancel && (
            <Button
              title="Cancel"
              onPress={onCancel}
              variant="outline"
              style={styles.cancelButton}
              disabled={loading}
            />
          )}
          <Button
            title={submitLabel}
            onPress={handleSubmit}
            variant="primary"
            loading={loading}
            disabled={loading}
            style={styles.submitButton}
          />
        </View>
        )}
      </View>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
  },
  form: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    ...designTokens.shadows.sm,
  },
  field: {
    marginBottom: spacing.md,
  },
  switchContainer: {
    marginBottom: spacing.xs,
  },
  switchLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  switchHelper: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  toggleContainer: {
    marginTop: spacing.sm,
    alignItems: 'flex-start',
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
});


